import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const INPUT = "w-full px-3 py-2.5 rounded-xl border border-[#D5CCBC] bg-white text-sm text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]";
const LABEL = "block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5";
const EMPTY = { label: "", desc: "", tag: "", image_url: "" };

export default function CategoriesAdmin() {
  const { categories, reload } = useCategories();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ label: c.label, desc: c.desc || "", tag: c.tag || "", image_url: c.image_url || "" });
    setOpen(true);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload?folder=categories", fd);
      setForm((x) => ({ ...x, image_url: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah gambar");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(editing ? "Kategori diperbarui" : "Kategori ditambahkan");
      setOpen(false);
      reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal menyimpan kategori");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.label}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Kategori dihapus");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus kategori");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= categories.length) return;
    const next = [...categories];
    [next[i], next[j]] = [next[j], next[i]];
    await api.put("/categories/reorder", { ids: next.map((c) => c.id) }).catch(() => toast.error("Gagal mengubah urutan"));
    reload();
  };

  return (
    <div data-testid="categories-admin">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Manajemen Kategori</h1>
          <p className="text-xs text-[#6B7C66] mt-1">Kategori tampil di halaman utama dan sebagai filter katalog. Produk harus memilih salah satu kategori.</p>
        </div>
        <button onClick={openNew} data-testid="category-add-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] text-[#FAF7EE] text-sm font-bold transition-colors">
          <Plus className="w-4 h-4" /> Tambah Kategori
        </button>
      </div>

      <div className="space-y-3">
        {categories.map((c, i) => (
          <div key={c.id} data-testid={`category-row-${c.key}`} className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-4 flex gap-4 items-center">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#EFE9DD] shrink-0">
              {c.image_url ? <img src={imageUrl(c.image_url)} alt={c.label} className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 m-auto mt-5 text-[#A39B8A]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif-display font-bold text-[#1E2B1D]">{c.label}</span>
                {c.tag && <span className="text-[10px] font-semibold bg-[#EAF2E9] text-[#3D5A40] px-2 py-0.5 rounded-full">{c.tag}</span>}
                <span data-testid={`category-count-${c.key}`} className="text-[10px] text-[#7E8F7A]">{c.product_count} produk</span>
              </div>
              <p className="text-xs text-[#6B7C66] mt-0.5 truncate">{c.desc}</p>
              <span className="text-[10px] font-mono text-[#A39B8A]">key: {c.key}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Naik" data-testid={`category-up-${c.key}`} className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] disabled:opacity-30 hover:bg-[#F4EFE6]"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} aria-label="Turun" data-testid={`category-down-${c.key}`} className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] disabled:opacity-30 hover:bg-[#F4EFE6]"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => openEdit(c)} aria-label="Edit" data-testid={`category-edit-${c.key}`} className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] hover:bg-[#F4EFE6]"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(c)} aria-label="Hapus" data-testid={`category-delete-${c.key}`} className="p-2 rounded-lg border border-[#D5CCBC] text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#FBF9F5]" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">{editing ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D5B]">Nama kategori tampil untuk pembeli; gambar & label kecil memperindah kartu kategori.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className={LABEL}>Nama Kategori</label>
              <input required minLength={2} value={form.label} onChange={set("label")} data-testid="category-label-input" placeholder="Contoh: Bunga Meja" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Deskripsi Singkat</label>
              <input value={form.desc} onChange={set("desc")} data-testid="category-desc-input" placeholder="Contoh: Rangkaian mungil untuk meja kerja & kafe" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Label Kecil (opsional)</label>
              <input value={form.tag} onChange={set("tag")} data-testid="category-tag-input" placeholder="Contoh: Favorit" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Gambar Kategori</label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#EFE9DD] border border-dashed border-[#C5BCAB] shrink-0">
                  {form.image_url && <img src={imageUrl(form.image_url)} alt="" className="w-full h-full object-cover" data-testid="category-image-preview" />}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="category-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="category-image-upload-btn" className="px-4 py-2 rounded-lg border border-[#3D5A40] text-[#3D5A40] text-xs font-semibold hover:bg-[#EAF2E9] disabled:opacity-50">
                  {uploading ? "Mengunggah..." : form.image_url ? "Ganti Gambar" : "Unggah Gambar"}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-[#D5CCBC] text-xs font-semibold text-[#5E6D5B]">Batal</button>
              <button type="submit" disabled={saving} data-testid="category-save-btn" className="px-5 py-2 rounded-lg bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-white text-xs font-bold">
                {saving ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
